import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { usePage } from '@inertiajs/react'
import { getDateLocale } from '@/lib/i18n'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { ArchiveProjectDialog } from '@/components/projects/ArchiveProjectDialog'
import { ExportToGithubDialog } from '@/components/projects/ExportToGithubDialog'
import { DesignConceptPicker, formatConceptName, type DesignConcept } from '@/components/projects/DesignConceptPicker'
import { ArchiveIcon, AlertCircleIcon, Github, Palette } from 'lucide-react'
import { isAdmin as checkIsAdmin } from '@/lib/auth'

interface Project {
  id: number
  name: string
  slug: string
  description?: string | null
  is_archived: boolean
  created_at: string
  updated_at: string
  project_type?: string
  django_stack?: boolean
  design_concept?: string | null
}

interface SettingsTabProps {
  project: Project
}

export function SettingsTab({ project }: SettingsTabProps) {
  const { t } = useTranslation(['conversations', 'projects'])
  const { props } = usePage<{
    auth: {
      user: {
        organization_role: string
      }
    }
    organization?: {
      current_plan: string
    }
    project?: {
      runtime_state: string | null
      instance_status: string | null
    }
    design_concepts?: DesignConcept[]
  }>()

  const isAdmin = checkIsAdmin(props.auth?.user)
  const isFreePlan = props.organization?.current_plan === 'free'
  const isSandboxActive = props.project?.runtime_state === 'running' && props.project?.instance_status === 'active'
  const canExport = isAdmin && !isFreePlan && isSandboxActive
  const isManagedAgentProject = project.project_type === 'managed_agent'
  const [isArchiveDialogOpen, setIsArchiveDialogOpen] = useState(false)
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false)
  const [isDesignDialogOpen, setIsDesignDialogOpen] = useState(false)

  const designConcepts = props.design_concepts || []

  // Auto-open export dialog if returning from GitHub App installation
  // URL cleanup is handled by parent (Chat.tsx)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('open_export') === 'true') {
      setIsExportDialogOpen(true)
    }
  }, [])

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Project Details */}
        <Card className="shadow-none">
          <CardHeader>
            <CardTitle>{t('settings.projectDetails')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">{t('settings.name')}</label>
              <p className="text-sm mt-1">{project.name}</p>
            </div>
            {project.description && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">{t('settings.description')}</label>
                <p className="text-sm mt-1">{project.description}</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-muted-foreground">{t('settings.created')}</label>
              <p className="text-sm mt-1">
                {new Date(project.created_at).toLocaleDateString(getDateLocale(), {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">{t('settings.lastUpdated')}</label>
              <p className="text-sm mt-1">
                {new Date(project.updated_at).toLocaleDateString(getDateLocale(), {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Design Style */}
        {!isManagedAgentProject && designConcepts.length > 0 && (
          <Card className="shadow-none">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette className="h-5 w-5" />
                {t('projects:settings.design.designStyle')}
              </CardTitle>
              <CardDescription>
                {t('projects:settings.design.designStyleDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  {project.design_concept ? (
                    <p className="text-sm font-medium">{formatConceptName(project.design_concept)}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground">{t('projects:settings.design.noneLabel')}</p>
                  )}
                </div>
                <Dialog open={isDesignDialogOpen} onOpenChange={setIsDesignDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      {t('projects:settings.design.change')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col overflow-hidden">
                    <DialogHeader>
                      <DialogTitle>{t('projects:settings.design.dialogTitle')}</DialogTitle>
                    </DialogHeader>
                    <DesignConceptPicker
                      concepts={designConcepts}
                      currentConcept={project.design_concept ?? null}
                      projectId={project.id}
                      onApplied={() => setIsDesignDialogOpen(false)}
                    />
                  </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Export Section - Django projects, admin only, non-archived */}
        {project.django_stack && isAdmin && !project.is_archived && (
          <Card className="shadow-none">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Github className="h-5 w-5" />
                {t('settings.exportTitle')}
              </CardTitle>
              <CardDescription>{t('settings.exportDescription')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {canExport ? (
                <>
                  <Button onClick={() => setIsExportDialogOpen(true)}>
                    <Github className="mr-2 h-4 w-4" />
                    {t('settings.exportButton')}
                  </Button>
                  <ExportToGithubDialog
                    open={isExportDialogOpen}
                    onOpenChange={setIsExportDialogOpen}
                    projectId={project.id}
                  />
                </>
              ) : (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-block">
                        <Button disabled>
                          <Github className="mr-2 h-4 w-4" />
                          {t('settings.exportButton')}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {isFreePlan
                          ? t('settings.exportUpgradeRequired')
                          : t('settings.exportSandboxRequired')}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                  {isFreePlan && (
                    <p className="text-sm text-muted-foreground">{t('settings.exportUpgradeRequired')}</p>
                  )}
                  {!isFreePlan && !isSandboxActive && (
                    <p className="text-sm text-muted-foreground">{t('settings.exportSandboxRequired')}</p>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Archive Section (only for non-archived projects) */}
        {!project.is_archived && (
          <Card className="shadow-none border-destructive/20">
            <CardHeader>
              <CardTitle className="text-destructive">{t('settings.dangerZone')}</CardTitle>
              <CardDescription>
                {t('settings.archiveDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isAdmin ? (
                <>
                  <Button variant="destructive" onClick={() => setIsArchiveDialogOpen(true)}>
                    <ArchiveIcon className="mr-2 h-4 w-4" />
                    {t('settings.archiveProject')}
                  </Button>
                  <ArchiveProjectDialog
                    project={project}
                    open={isArchiveDialogOpen}
                    onOpenChange={setIsArchiveDialogOpen}
                  />
                </>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-block">
                      <Button variant="destructive" disabled>
                        <ArchiveIcon className="mr-2 h-4 w-4" />
                        {t('settings.archiveProject')}
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('settings.adminOnlyTooltip')}</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </CardContent>
          </Card>
        )}

        {/* Archived Notice */}
        {project.is_archived && (
          <Card className="shadow-none border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/30">
            <CardContent className="flex items-start gap-3 py-4">
              <AlertCircleIcon className="h-5 w-5 text-amber-600 dark:text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-amber-800 dark:text-amber-200">{t('settings.archivedTitle')}</p>
                <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                  {t('settings.archivedDescription')}{' '}
                  <a
                    href="mailto:support@cayu.app"
                    className="font-medium underline hover:no-underline"
                  >
                    support@cayu.app
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
